import { Component, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { AnimationEvent } from '@angular/animations';

import { fromEvent, Subject } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { filter, tap, takeUntil } from 'rxjs/operators';

import { PopupMenuAnimationState, popupMenuAnimations } from '../cog/popup-menu-animations';
import { PopupMenuRef } from '../cog/popup-menu.ref';
import { ColoredPopupMenuItem } from '../cog/colored-popup-menu-item.model';
import * as fromRootStore from 'src/app/_apps/root/store';
import { TypePopup } from 'src/app/_apps/app.constants';

@Component({
    selector: 'aa-colored-popup-menu',
    templateUrl: './colored-popup-menu.component.html',
    styleUrls: [
        './colored-popup-menu.component.scss'
    ],
    animations: [
        popupMenuAnimations.showMenu
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ColoredPopupMenuComponent implements OnInit, OnDestroy
{
    private destroyator = new Subject();
    private intervalId: any;
    private boundingClientRect: ClientRect;
    popupMenuItems: ColoredPopupMenuItem[];
    height: number;
    width: number;
    animationState: PopupMenuAnimationState = 'hide';
    typePopup: TypePopup = 'popover';

    constructor (
        private popupMenuRef: PopupMenuRef,
        private actions: Actions
    )
    {
        this.popupMenuItems = this.popupMenuRef.popupMenuItems as ColoredPopupMenuItem[];
        this.popupMenuRef.overlayRef
            .keydownEvents()
            .pipe(
                filter((event: KeyboardEvent) =>
                    event.key === 'Escape'
                ),
                tap((event: KeyboardEvent) =>
                    event.stopImmediatePropagation()
                ),
                tap((_) =>
                    this.animationState = 'hide'
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
        fromEvent(document, 'click')
            .pipe(
                filter((event: MouseEvent) => this.clickOutside(event)),
                tap((_) =>
                    this.animationState = 'hide'
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
        this.actions
            .pipe(
                ofType(fromRootStore.RootMiscStoreActions.RootPopupMenuItemClicked),
                tap((_) =>
                    this.animationState = 'hide'
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    ngOnInit ()
    {
        const width = parseInt(getComputedStyle(document.documentElement)
            .getPropertyValue('--user-menu-width'), 10);

        this.height = this.popupMenuRef.config.height;
        this.width = this.popupMenuRef.config.width ? this.popupMenuRef.config.width : width;
        this.typePopup = this.popupMenuRef.config.typePopup;

        Promise.resolve(null).then(() =>
        {
            this.animationState = 'show';
            this.intervalId = setTimeout(() => this.animationState = 'hide', 2500);
        });
    }

    ngOnDestroy (): void
    {
        clearTimeout(this.intervalId);
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    @HostListener('mouseout') waitForClosing ()
    {
        clearTimeout(this.intervalId);
        this.intervalId = setTimeout(() => this.animationState = 'hide', 2500);
    }

    @HostListener('mouseover') stopWaitingForClosing ()
    {
        clearTimeout(this.intervalId);
    }

    public onShowHide (event: AnimationEvent)
    {
        const { phaseName, fromState, toState } = event;

        if (fromState === 'hide' && toState === 'show' && phaseName === 'done')
        {
            this.boundingClientRect = this.popupMenuRef.myGetBoundingClientRect();
        }

        if (fromState === 'show' && toState === 'hide' && phaseName === 'done')
        {
            this.popupMenuRef.close();
        }
    }

    private clickOutside (event: any): boolean
    {
        if (this.boundingClientRect !== undefined)
        {
            const { top, bottom, left, right } = this.boundingClientRect;
            const { x, y } = event;
            const isInsideX = x >= left && x <= right;
            const isInsideY = y >= top && y <= bottom;

            return !(isInsideX && isInsideY);
        }
    }
}
